import type { ExerciseTemplate } from '@yuan-math/exercises';
import type { Figure } from '@yuan-math/curriculum';
import { add, fraction, fromMixed, gcd, roundTo, sub, toLatex, toText, type Fraction } from '@yuan-math/math';
import {
  choiceExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * Grade 5.
 *
 * `arithmetic.ts`, `fractions.ts`, `decimals.ts` and `applied.ts` carry the
 * spine Grade 5 shares with the grades either side of it. These templates are
 * the year's own work, and the year has a single character: the whole number
 * stops being the unit. Multiplying by a hundred becomes a move rather than a
 * calculation, a fraction bar becomes a division sign, multiplication is
 * allowed to shrink and division to grow, and a solid is measured by filling
 * rather than covering.
 *
 * Three habits run through the file.
 *
 * Every decimal is built from an integer and a place count — see `dec` — rather
 * than written as a literal. `0.24 * 0.3` is `0.07199999999999999` in binary
 * floating point, and a generated exercise whose stated answer is that is a
 * question no student can answer correctly.
 *
 * Every distractor is an error a Grade 5 teacher actually sees. Most of them
 * are the *same* error wearing different clothes: an answer whose digits are
 * perfect and whose size is out by a power of ten. That is precisely the case
 * where "incorrect" tells a student nothing, which is why almost every template
 * here names which power of ten went missing and why.
 *
 * And no template asks for a quantity whose unit it has not put in the prompt,
 * because "60" and "60 cm³" answer different questions.
 */

/* -------------------------------------------------------------------------- */
/* Small shared helpers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A decimal built from an integer and a number of places, free of binary dust.
 *
 * Every decimal in this file goes through here. Writing `2.35 * 4.5` directly
 * gives 10.575000000000001, and `String(...)` of that is what the content test
 * submits as the correct answer — so the exercise would be unanswerable by
 * construction.
 */
const dec = (digits: number, places: number): number => roundTo(digits / 10 ** places, places);

/** How a decimal is written in a prompt: `2.5`, not `2.50` and not `2.5000001`. */
const show = (value: number): string => String(value);

/** An integer of `n` digits whose last digit is not zero, so it shows all `n`. */
function decimalDigits(rng: { int(min: number, max: number): number }, count: number): number {
  let value = rng.int(1, 9);
  for (let i = 1; i < count - 1; i += 1) value = value * 10 + rng.int(0, 9);
  return count === 1 ? value : value * 10 + rng.int(1, 9);
}

/** The names of the decimal places, for rounding questions. */
const PLACE_NAMES = ['whole number', 'tenth', 'hundredth'];

/** Contexts for decimal multiplication, so the prose is not all "work out". */
const MEASURE_CONTEXTS: { subject: string; unit: string; per: string }[] = [
  { subject: 'One litre of paint covers', unit: 'm²', per: 'litres' },
  { subject: 'One metre of cable weighs', unit: 'kg', per: 'metres' },
  { subject: 'One bag of sand fills', unit: 'litres', per: 'bags' },
];

/** Objects whose measurement is worth rounding, each with the unit that fits it. */
const ROUNDING_CONTEXTS: { clause: string; unit: string }[] = [
  { clause: 'a parcel weighs', unit: 'kg' },
  { clause: 'a jug holds', unit: 'litres' },
  { clause: 'a plank measures', unit: 'metres' },
];

/** Names for sharing and cutting problems. */
const NAMES = ['Maya', 'Ravi', 'Omar', 'Lena', 'Sofia', 'Kai', 'Noor', 'Ellis', 'Jonah', 'Priya', 'Tomas', 'Aisha'];

/** The numerators below `d` that leave a fraction already in lowest terms. */
const coprimeNumerators = (d: number): number[] => {
  const out: number[] = [];
  for (let n = 1; n < d; n += 1) if (gcd(n, d) === 1) out.push(n);
  return out;
};

/**
 * Pairs of numerators over `d` whose sum passes a whole, with every fraction in
 * the question and its answer already in lowest terms.
 *
 * All three conditions earn their place. The sum has to overflow or there is no
 * regrouping to get wrong. An unsimplified *starting* fraction gives the game
 * away. And the regrouped remainder has to keep the denominator `d` — with
 * 5 3/4 + 5 3/4 the answer reduces to 11 1/2, and a student can then pick the
 * right option by noticing that it is the only one written in halves, without
 * regrouping anything.
 *
 * Only odd denominators survive all three, which is why the caller offers
 * thirds, fifths, sevenths and ninths: for even `d`, two numerators coprime to
 * it are both odd, so their sum less `d` is always even and shares a factor.
 */
function overflowingPairs(d: number): [number, number][] {
  const out: [number, number][] = [];
  for (let n1 = 1; n1 < d; n1 += 1) {
    if (gcd(n1, d) !== 1) continue;
    for (let n2 = n1; n2 < d; n2 += 1) {
      if (gcd(n2, d) !== 1) continue;
      if (n1 + n2 <= d) continue;
      if (gcd(n1 + n2 - d, d) !== 1) continue;
      out.push([n1, n2]);
    }
  }
  return out;
}

/** `4` → `4`, `7/4` → `1 3/4`: how a student is asked to write a mixed answer. */
const mixedText = (value: Fraction): string => {
  if (value.d === 1) return String(value.n);
  if (Math.abs(value.n) < value.d) return toText(value);
  const whole = Math.floor(value.n / value.d);
  return `${whole} ${value.n - whole * value.d}/${value.d}`;
};

/**
 * The shape families, in order of how much they demand.
 *
 * `contains` lists the families a member of this one always belongs to as well.
 * The whole of the classification topic is that this relation runs one way:
 * every square is a rectangle and no rectangle need be a square, and a student
 * who reverses it has made a specific, nameable error.
 */
const FAMILIES: { id: string; name: string; plural: string; contains: string[] }[] = [
  { id: 'square', name: 'square', plural: 'squares', contains: ['rectangle', 'rhombus', 'parallelogram', 'trapezium', 'kite', 'quadrilateral'] },
  { id: 'rectangle', name: 'rectangle', plural: 'rectangles', contains: ['parallelogram', 'trapezium', 'quadrilateral'] },
  { id: 'rhombus', name: 'rhombus', plural: 'rhombuses', contains: ['parallelogram', 'trapezium', 'kite', 'quadrilateral'] },
  { id: 'parallelogram', name: 'parallelogram', plural: 'parallelograms', contains: ['trapezium', 'quadrilateral'] },
  { id: 'trapezium', name: 'trapezium', plural: 'trapeziums', contains: ['quadrilateral'] },
  { id: 'kite', name: 'kite', plural: 'kites', contains: ['quadrilateral'] },
  { id: 'quadrilateral', name: 'quadrilateral', plural: 'quadrilaterals', contains: [] },
];

/** Properties that some families guarantee and others do not. */
const PROPERTIES: { text: string; holds: string[] }[] = [
  { text: 'four right angles', holds: ['square', 'rectangle'] },
  { text: 'four equal sides', holds: ['square', 'rhombus'] },
  { text: 'two pairs of parallel sides', holds: ['square', 'rectangle', 'rhombus', 'parallelogram'] },
  { text: 'at least one line of symmetry', holds: ['square', 'rectangle', 'rhombus', 'kite'] },
];

/** A description precise enough to name exactly one family, and names that are simply false for it. */
const DESCRIPTIONS: { properties: string; answer: string; wrong: { name: string; misconceptionId: string }[] }[] = [
  {
    properties: 'four equal sides and no right angles',
    answer: 'rhombus',
    wrong: [
      { name: 'square', misconceptionId: 'hierarchy-inverted' },
      { name: 'rectangle', misconceptionId: 'shape-named-by-look' },
    ],
  },
  {
    properties: 'four right angles, with the sides next to each other different lengths',
    answer: 'rectangle',
    wrong: [
      { name: 'square', misconceptionId: 'hierarchy-inverted' },
      { name: 'rhombus', misconceptionId: 'shape-named-by-look' },
    ],
  },
  {
    properties: 'both pairs of opposite sides parallel, no right angles, and its sides not all equal',
    answer: 'parallelogram',
    wrong: [
      { name: 'rectangle', misconceptionId: 'shape-named-by-look' },
      { name: 'rhombus', misconceptionId: 'hierarchy-inverted' },
    ],
  },
  {
    properties: 'exactly one pair of parallel sides',
    answer: 'trapezium',
    wrong: [
      { name: 'parallelogram', misconceptionId: 'hierarchy-inverted' },
      { name: 'rectangle', misconceptionId: 'shape-named-by-look' },
    ],
  },
  {
    properties: 'two pairs of equal sides next to each other, and no pair of parallel sides',
    answer: 'kite',
    wrong: [
      { name: 'rhombus', misconceptionId: 'hierarchy-inverted' },
      { name: 'parallelogram', misconceptionId: 'shape-named-by-look' },
    ],
  },
];

const prismFigure = (length: number, width: number, height: number): Figure => ({
  kind: 'prism',
  length,
  width,
  height,
  labels: { length: `${length} cm`, width: `${width} cm`, height: `${height} cm` },
});

export const GRADE_FIVE_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------- powers of ten */
  template({
    id: 'power-of-ten-multiply',
    conceptId: 'powers-of-ten',
    description: 'Multiply a decimal by a power of ten.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const places = difficulty === 1 ? 1 : 2;
      const digits = decimalDigits(rng, difficulty === 1 ? 2 : 3);
      const power = rng.int(1, difficulty === 3 ? 3 : 2);
      const start = dec(digits, places);
      const answer = dec(digits * 10 ** power, places);
      // "One zero too few" has no meaning at a single zero, so miscount upwards there.
      const miscount = power === 1 ? 2 : power - 1;
      return numericExercise({
        prompt: 'Work out this multiplication.',
        latex: `${show(start)} \\times 10^{${power}}`,
        value: answer,
        summary: `10^${power} is ${10 ** power}, so every digit of ${show(start)} moves ${power} place${power === 1 ? '' : 's'} to the left: ${show(answer)}.`,
        hint: 'Multiplying makes a number bigger, so the digits move towards the larger places. How many places?',
        steps: [
          { label: 'Read the power.', latex: `10^{${power}} = ${10 ** power}` },
          { label: 'Multiplying makes it larger, so the digits move left.', latex: `${power} \\text{ place${power === 1 ? '' : 's'}}` },
          { label: 'Move them.', latex: `${show(start)} \\times 10^{${power}} = ${show(answer)}` },
        ],
        traps: [
          trap(dec(digits * 10 ** miscount, places), 'power-of-ten-zeros-miscounted'),
          trap(dec(digits, places + power), 'decimal-shift-inverted'),
        ],
      });
    },
  }),

  template({
    id: 'power-of-ten-divide',
    conceptId: 'powers-of-ten',
    description: 'Divide a decimal by a power of ten.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const places = difficulty === 2 ? 1 : 2;
      const digits = decimalDigits(rng, difficulty === 2 ? 2 : 3);
      const power = rng.int(1, difficulty === 4 ? 3 : 2);
      const start = dec(digits, places);
      const answer = dec(digits, places + power);
      const miscount = power === 1 ? 2 : power - 1;
      return numericExercise({
        prompt: 'Work out this division.',
        latex: `${show(start)} \\div 10^{${power}}`,
        value: answer,
        summary: `Dividing by ${10 ** power} makes the number smaller, so every digit moves ${power} place${power === 1 ? '' : 's'} to the right: ${show(answer)}.`,
        hint: 'Should the answer be bigger or smaller than what you started with? Decide that before you move anything.',
        steps: [
          { label: 'Read the power.', latex: `10^{${power}} = ${10 ** power}` },
          { label: 'Dividing makes it smaller, so the digits move right.', latex: `${power} \\text{ place${power === 1 ? '' : 's'}}` },
          { label: 'Move them, filling with zeros.', latex: `${show(start)} \\div 10^{${power}} = ${show(answer)}` },
        ],
        traps: [
          trap(dec(digits, places + miscount), 'power-of-ten-zeros-miscounted'),
          trap(dec(digits * 10 ** power, places), 'decimal-shift-inverted'),
        ],
      });
    },
  }),

  template({
    id: 'power-of-ten-find-exponent',
    conceptId: 'powers-of-ten',
    description: 'Find the power of ten that turns one number into another.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const places = difficulty === 3 ? 1 : 2;
      const digits = decimalDigits(rng, 3);
      const power = rng.int(2, difficulty === 5 ? 4 : 3);
      const start = dec(digits, places);
      const result = dec(digits * 10 ** power, places);
      return numericExercise({
        prompt: 'What is the missing exponent?',
        latex: `${show(start)} \\times 10^{?} = ${show(result)}`,
        value: power,
        summary: `The digits moved ${power} places to the left, so the multiplier was 10^${power} = ${10 ** power}.`,
        hint: 'Count how many places every digit has moved.',
        steps: [
          { label: 'Compare the two numbers place by place.', latex: `${show(start)} \\rightarrow ${show(result)}` },
          { label: 'Count the places moved.', latex: String(power) },
          { label: 'That is the exponent.', latex: `10^{${power}} = ${10 ** power}` },
        ],
        // Giving the multiplier itself rather than the exponent — the classic
        // confusion between 10^3 and 1000, and between 10^3 and 30.
        traps: [trap(10 ** power, 'power-of-ten-zeros-miscounted')],
      });
    },
  }),

  /* -------------------------------------------------- rounding decimals */
  template({
    id: 'round-decimal-to-place',
    conceptId: 'round-decimals',
    description: 'Round a decimal to a named place.',
    difficulties: [1, 2, 3, 4],
    build: ({ rng, difficulty }) => {
      const place = difficulty === 1 ? rng.int(0, 1) : rng.int(0, 2);
      const totalPlaces = place + 2;
      const whole = rng.int(1, difficulty >= 3 ? 99 : 9);

      const fracDigits: number[] = [];
      for (let i = 0; i < totalPlaces - 1; i += 1) fracDigits.push(rng.int(0, 9));
      fracDigits.push(rng.int(1, 9)); // never a trailing zero, or the number loses a digit

      const scaled = fracDigits.reduce((acc, digit) => acc * 10 + digit, whole);
      const value = dec(scaled, totalPlaces);

      const step = 10 ** (totalPlaces - place);
      const answer = dec(Math.round(scaled / step), place);
      // Rounding a place at a time, so a digit two along gets a vote it should not have.
      const chained = dec(Math.round(Math.round(scaled / (step / 10)) / 10), place);
      // Deciding on the digit one further right than the one that decides.
      const truncated = Math.floor(scaled / step);
      const misread = dec(truncated + ((fracDigits[place + 1] as number) >= 5 ? 1 : 0), place);

      const traps = [];
      if (chained !== answer) traps.push(trap(chained, 'rounding-chained'));
      if (misread !== answer && misread !== chained) traps.push(trap(misread, 'rounding-place-misread'));

      return numericExercise({
        prompt: `Round ${show(value)} to the nearest ${PLACE_NAMES[place]}.`,
        value: answer,
        summary: `The digit just after the ${PLACE_NAMES[place]}s place is ${fracDigits[place]}, so ${show(value)} rounds to ${show(answer)}.`,
        hint: `Find the ${PLACE_NAMES[place]}s place, then look at the single digit immediately to its right.`,
        steps: [
          { label: `Keep up to the ${PLACE_NAMES[place]}s place.`, latex: show(dec(truncated, place)) },
          { label: 'One digit decides, and it is the very next one.', latex: String(fracDigits[place]) },
          { label: (fracDigits[place] as number) >= 5 ? 'It is 5 or more, so round up.' : 'It is less than 5, so leave the digit alone.', latex: show(answer) },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'round-decimal-deciding-digit',
    conceptId: 'round-decimals',
    description: 'Identify the digit that decides a rounding.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const place = difficulty === 1 ? rng.int(0, 1) : rng.int(0, 2);
      const whole = rng.int(1, 9);
      // Four distinct digits, so the four options can never coincide.
      const fracDigits = rng.shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 4);
      const scaled = fracDigits.reduce((acc, digit) => acc * 10 + digit, whole);
      const value = dec(scaled, 4);

      const entries = fracDigits.map((digit, index) => ({
        label: String(digit),
        correct: index === place,
        ...(index === place ? {} : { misconceptionId: 'rounding-place-misread' }),
      }));
      const { choices, correctId } = options(entries, rng.shuffle);

      return choiceExercise({
        prompt: `Which digit of ${show(value)} decides how it rounds to the nearest ${PLACE_NAMES[place]}?`,
        choices,
        correctId,
        summary: `Rounding to the nearest ${PLACE_NAMES[place]} is decided by the digit immediately to the right of that place, which is ${fracDigits[place]}.`,
        hint: 'Put your finger on the place you are rounding to, then move one digit right.',
        steps: [
          { label: `Find the ${PLACE_NAMES[place]}s place.`, latex: show(dec(Math.floor(scaled / 10 ** (4 - place)), place)) },
          { label: 'Move one digit to the right.', latex: String(fracDigits[place]) },
        ],
      });
    },
  }),

  template({
    id: 'round-decimal-in-context',
    conceptId: 'round-decimals',
    description: 'Round a measurement to a named place.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const place = rng.int(1, 2);
      const totalPlaces = place + 2;
      const whole = rng.int(1, difficulty === 2 ? 9 : 49);
      const fracDigits: number[] = [];
      for (let i = 0; i < totalPlaces - 1; i += 1) fracDigits.push(rng.int(0, 9));
      fracDigits.push(rng.int(1, 9));

      const scaled = fracDigits.reduce((acc, digit) => acc * 10 + digit, whole);
      const value = dec(scaled, totalPlaces);
      const step = 10 ** (totalPlaces - place);
      const answer = dec(Math.round(scaled / step), place);
      const chained = dec(Math.round(Math.round(scaled / (step / 10)) / 10), place);

      const name = rng.pick(NAMES);
      const { clause, unit } = rng.pick(ROUNDING_CONTEXTS);
      const traps = chained === answer ? [] : [trap(chained, 'rounding-chained')];

      return numericExercise({
        prompt: `${name} finds that ${clause} ${show(value)} ${unit}. What is that to the nearest ${PLACE_NAMES[place]}?`,
        unit,
        value: answer,
        summary: `The digit after the ${PLACE_NAMES[place]}s place is ${fracDigits[place]}, so ${show(value)} rounds to ${show(answer)}.`,
        hint: 'Round straight to the place you were asked for, in one step, from the number you were given.',
        steps: [
          { label: 'One digit decides.', latex: String(fracDigits[place]) },
          { label: (fracDigits[place] as number) >= 5 ? 'Round up.' : 'Leave the digit alone.', latex: `${show(answer)} \\text{ ${unit}}` },
        ],
        traps,
      });
    },
  }),

  /* ------------------------------------------------ multiplying decimals */
  template({
    id: 'multiply-decimals-basic',
    conceptId: 'multiply-decimals',
    description: 'Multiply two decimals.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const placesA = 1;
      const placesB = difficulty === 2 ? 1 : 2;
      const digitsA = decimalDigits(rng, 2);
      const digitsB = decimalDigits(rng, difficulty === 2 ? 2 : 3);
      const a = dec(digitsA, placesA);
      const b = dec(digitsB, placesB);
      const product = digitsA * digitsB;
      const answer = dec(product, placesA + placesB);
      return numericExercise({
        prompt: 'Multiply.',
        latex: `${show(a)} \\times ${show(b)}`,
        value: answer,
        summary: `${digitsA} × ${digitsB} = ${product}, and the two factors have ${placesA} + ${placesB} = ${placesA + placesB} decimal places between them, so the answer is ${show(answer)}.`,
        hint: 'Multiply as though both numbers were whole, then count the decimal places in the two factors and add them.',
        steps: [
          { label: 'Ignore the points and multiply.', latex: `${digitsA} \\times ${digitsB} = ${product}` },
          { label: 'Count the decimal places in each factor.', latex: `${placesA} + ${placesB} = ${placesA + placesB}` },
          { label: 'Put that many places into the answer.', latex: show(answer) },
        ],
        traps: [
          trap(product, 'decimal-point-not-replaced'),
          trap(dec(product, Math.max(placesA, placesB)), 'decimal-places-not-summed'),
        ],
      });
    },
  }),

  template({
    id: 'multiply-decimals-from-whole',
    conceptId: 'multiply-decimals',
    description: 'Place the point in a product, given the whole-number multiplication.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const placesA = difficulty === 1 ? 1 : rng.int(1, 2);
      const placesB = difficulty === 3 ? 2 : 1;
      const digitsA = decimalDigits(rng, placesA + 1);
      const digitsB = decimalDigits(rng, placesB + 1);
      const a = dec(digitsA, placesA);
      const b = dec(digitsB, placesB);
      const product = digitsA * digitsB;
      const answer = dec(product, placesA + placesB);
      return numericExercise({
        prompt: `You are told that ${digitsA} × ${digitsB} = ${product}. Use it to work out the multiplication below.`,
        latex: `${show(a)} \\times ${show(b)}`,
        value: answer,
        summary: `The digits are already known. ${placesA} place plus ${placesB} place${placesB === 1 ? '' : 's'} makes ${placesA + placesB}, so the answer is ${show(answer)}.`,
        hint: 'The digits do not change. All that is left is deciding where the point goes.',
        steps: [
          { label: 'The whole-number product is given.', latex: String(product) },
          { label: 'Add the decimal places of the two factors.', latex: `${placesA} + ${placesB} = ${placesA + placesB}` },
          { label: 'Count that many places back in from the right.', latex: show(answer) },
        ],
        traps: [
          trap(product, 'decimal-point-not-replaced'),
          trap(dec(product, Math.max(placesA, placesB)), 'decimal-places-not-summed'),
        ],
      });
    },
  }),

  template({
    id: 'multiply-decimals-context',
    conceptId: 'multiply-decimals',
    description: 'Multiply decimals in a measurement context.',
    difficulties: [3, 4, 5],
    build: ({ rng }) => {
      const context = rng.pick(MEASURE_CONTEXTS);
      const rateDigits = decimalDigits(rng, 3);
      const amountDigits = decimalDigits(rng, 2);
      const rate = dec(rateDigits, 2);
      const amount = dec(amountDigits, 1);
      const product = rateDigits * amountDigits;
      const answer = dec(product, 3);
      return numericExercise({
        prompt: `${context.subject} ${show(rate)} ${context.unit}. How much for ${show(amount)} ${context.per}?`,
        unit: context.unit,
        value: answer,
        summary: `${rateDigits} × ${amountDigits} = ${product}, with 2 + 1 = 3 decimal places, giving ${show(answer)} ${context.unit}.`,
        hint: 'Multiply as whole numbers first, then count three decimal places back in.',
        steps: [
          { label: 'Multiply as whole numbers.', latex: `${rateDigits} \\times ${amountDigits} = ${product}` },
          { label: 'Two places plus one place.', latex: '2 + 1 = 3' },
          { label: 'Place the point.', latex: `${show(answer)} \\text{ ${context.unit}}` },
        ],
        traps: [
          trap(product, 'decimal-point-not-replaced'),
          trap(dec(product, 2), 'decimal-places-not-summed'),
        ],
      });
    },
  }),

  /* -------------------------------------------------- dividing decimals */
  template({
    id: 'divide-decimal-by-whole',
    conceptId: 'divide-decimals',
    description: 'Divide a decimal by a whole number.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(2, difficulty === 1 ? 5 : 9);
      const places = difficulty === 3 ? 2 : 1;
      const quotientDigits = decimalDigits(rng, difficulty === 1 ? 2 : 3);
      const quotient = dec(quotientDigits, places);
      const dividend = dec(quotientDigits * divisor, places);
      return numericExercise({
        prompt: 'Divide.',
        latex: `${show(dividend)} \\div ${divisor}`,
        value: quotient,
        summary: `${quotientDigits * divisor} ÷ ${divisor} = ${quotientDigits}, and the point comes straight up, giving ${show(quotient)}.`,
        hint: 'Divide as though the point were not there, then bring the point straight up into the answer.',
        steps: [
          { label: 'Divide the digits.', latex: `${quotientDigits * divisor} \\div ${divisor} = ${quotientDigits}` },
          { label: 'The point comes straight up.', latex: show(quotient) },
          { label: 'Check by multiplying back.', latex: `${show(quotient)} \\times ${divisor} = ${show(dividend)}` },
        ],
        // Doing the division and forgetting to put the point back.
        traps: [trap(quotientDigits, 'place-value-shift')],
      });
    },
  }),

  template({
    id: 'divide-by-decimal',
    conceptId: 'divide-decimals',
    description: 'Divide by a decimal, shifting both numbers.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const divisorPlaces = difficulty === 3 ? 1 : rng.int(1, 2);
      const divisorDigits = decimalDigits(rng, divisorPlaces + 1);
      const quotientDigits = decimalDigits(rng, 2);
      const divisor = dec(divisorDigits, divisorPlaces);
      const quotient = dec(quotientDigits, 1);
      const dividend = dec(divisorDigits * quotientDigits, divisorPlaces + 1);
      return numericExercise({
        prompt: 'Divide.',
        latex: `${show(dividend)} \\div ${show(divisor)}`,
        value: quotient,
        summary: `Shifting both numbers ${divisorPlaces} place${divisorPlaces === 1 ? '' : 's'} makes the divisor whole, and ${show(dividend)} ÷ ${show(divisor)} = ${show(quotient)}.`,
        hint: 'Move the point in both numbers by the same amount, until the number you are dividing by is whole.',
        steps: [
          { label: 'The divisor needs to become whole.', latex: `${show(divisor)} \\rightarrow ${divisorDigits}` },
          { label: 'Move the dividend by the same number of places.', latex: `${show(dividend)} \\rightarrow ${show(dec(divisorDigits * quotientDigits, 1))}` },
          { label: 'Now divide.', latex: `${show(dec(divisorDigits * quotientDigits, 1))} \\div ${divisorDigits} = ${show(quotient)}` },
          { label: 'Check by multiplying back.', latex: `${show(quotient)} \\times ${show(divisor)} = ${show(dividend)}` },
        ],
        // Clearing the divisor and leaving the dividend where it was.
        traps: [trap(dec(quotientDigits, 1 + divisorPlaces), 'divisor-decimal-not-cleared')],
      });
    },
  }),

  template({
    id: 'divide-decimals-equivalent',
    conceptId: 'divide-decimals',
    description: 'Choose the equivalent division with a whole-number divisor.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const divisorPlaces = difficulty === 2 ? 1 : rng.int(1, 2);
      const divisorDigits = decimalDigits(rng, divisorPlaces + 1);
      const dividendDigits = decimalDigits(rng, 3);
      const divisor = dec(divisorDigits, divisorPlaces);
      const dividend = dec(dividendDigits, 2);

      const { choices, correctId } = options(
        [
          { label: `${show(dec(dividendDigits, 2 - divisorPlaces))} ÷ ${divisorDigits}`, correct: true },
          { label: `${show(dividend)} ÷ ${divisorDigits}`, misconceptionId: 'divisor-decimal-not-cleared' },
          // One place too far: right idea, wrong number of zeros. Shifting the
          // dividend by `divisorPlaces` is what makes it correct, so the
          // over-shift has to be built from that, never from the raw digits —
          // at two decimal places those are the same number.
          { label: `${show(dec(dividendDigits * 10, 2 - divisorPlaces))} ÷ ${divisorDigits}`, misconceptionId: 'power-of-ten-zeros-miscounted' },
          { label: `${show(dec(dividendDigits, 3))} ÷ ${divisorDigits}`, misconceptionId: 'decimal-shift-inverted' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which division has the same answer as ${show(dividend)} ÷ ${show(divisor)}?`,
        choices,
        correctId,
        summary: `Both numbers move ${divisorPlaces} place${divisorPlaces === 1 ? '' : 's'} to the left, which makes the divisor whole and leaves the answer unchanged.`,
        hint: 'Whatever you do to the number you are dividing by, do to the number being divided.',
        steps: [
          { label: 'Make the divisor whole.', latex: `${show(divisor)} \\times 10^{${divisorPlaces}} = ${divisorDigits}` },
          { label: 'Do the same to the dividend.', latex: `${show(dividend)} \\times 10^{${divisorPlaces}} = ${show(dec(dividendDigits, 2 - divisorPlaces))}` },
        ],
      });
    },
  }),

  /* ------------------------------------------------ a fraction is a division */
  template({
    id: 'fraction-as-division-share',
    conceptId: 'fraction-as-division',
    description: 'Write the answer to a sharing problem as a fraction.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const people = rng.int(3, difficulty === 1 ? 5 : 9);
      const items = rng.pick(coprimeNumerators(people));
      const name = rng.pick(NAMES);
      const answer = fraction(items, people);
      return fractionExercise({
        prompt: `${name} shares ${items} pizzas equally between ${people} children. How much pizza does each child get?`,
        value: answer,
        summary: `Sharing ${items} between ${people} is the division ${items} ÷ ${people}, and the answer to that division is the fraction ${toText(answer)}.`,
        hint: 'Cut every pizza into as many pieces as there are children, then count the pieces one child receives.',
        steps: [
          { label: 'Write the sharing as a division.', latex: `${items} \\div ${people}` },
          { label: 'A fraction bar is a division sign.', latex: toLatex(answer) },
          { label: 'Check: that much, ' + `${people} times over, is ${items}.`, latex: `${people} \\times ${toLatex(answer)} = ${items}` },
        ],
        traps: [fractionTrap(fraction(people, items), 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'division-to-mixed-number',
    conceptId: 'fraction-as-division',
    description: 'Write a whole-number division as a mixed number.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(3, difficulty === 2 ? 5 : 9);
      const wholePart = rng.int(2, difficulty === 4 ? 12 : 6);
      const remainder = rng.pick(coprimeNumerators(divisor));
      const dividend = wholePart * divisor + remainder;
      const answer = fraction(dividend, divisor);
      return fractionExercise({
        prompt: 'Work out this division, writing your answer as a mixed number.',
        latex: `${dividend} \\div ${divisor}`,
        value: answer,
        summary: `${dividend} ÷ ${divisor} is ${wholePart} remainder ${remainder}, and ${remainder === 1 ? 'the one left over is' : `the ${remainder} left over are`} ${remainder}/${divisor} of another whole.`,
        hint: 'Do the division with a remainder first. What fraction of one whole is that remainder?',
        steps: [
          { label: 'Divide, keeping the remainder.', latex: `${dividend} \\div ${divisor} = ${wholePart} \\text{ r } ${remainder}` },
          { label: 'The remainder is that many pieces of the size you divided into.', latex: `\\frac{${remainder}}{${divisor}}` },
          { label: 'Write the mixed number.', latex: `${wholePart}\\tfrac{${remainder}}{${divisor}}` },
        ],
        traps: [
          trap(wholePart, 'ignored-remainder'),
          fractionTrap(fromMixed(wholePart, divisor, remainder), 'whole-number-fraction-confused'),
        ],
      });
    },
  }),

  template({
    id: 'fraction-as-division-interpret',
    conceptId: 'fraction-as-division',
    description: 'Choose the exact answer to a sharing problem.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(4, difficulty === 2 ? 6 : 9);
      const wholePart = rng.int(2, 9);
      // At least two left over, so the swapped-parts distractor reads as a
      // mixed number a student might actually write. `5 3/1` is not one. And
      // in lowest terms, so the correct option is not the only simplified one.
      const remainder = rng.pick(coprimeNumerators(divisor).filter((value) => value >= 2));
      const dividend = wholePart * divisor + remainder;
      const name = rng.pick(NAMES);

      const { choices, correctId } = options(
        [
          { label: `${wholePart} ${remainder}/${divisor} m`, correct: true },
          { label: `${wholePart} m`, misconceptionId: 'ignored-remainder' },
          { label: `${wholePart} ${divisor}/${remainder} m`, misconceptionId: 'whole-number-fraction-confused' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${name} cuts a ${dividend} m rope into ${divisor} equal pieces. How long is each piece?`,
        choices,
        correctId,
        summary: `${dividend} ÷ ${divisor} = ${wholePart} remainder ${remainder}, and the ${remainder} metres left over are shared as ${remainder}/${divisor} of a metre each.`,
        hint: 'The rope does not stop existing because the division was not exact. What happens to the part left over?',
        steps: [
          { label: 'Share out the whole metres.', latex: `${wholePart} \\text{ m each}` },
          { label: 'Share the remainder as well.', latex: `${remainder} \\div ${divisor} = \\frac{${remainder}}{${divisor}}` },
          { label: 'Put the two parts together.', latex: `${wholePart}\\tfrac{${remainder}}{${divisor}} \\text{ m}` },
        ],
      });
    },
  }),

  /* ------------------------------------------------------- mixed numbers */
  template({
    id: 'add-mixed-unlike',
    conceptId: 'add-mixed-numbers',
    description: 'Add two mixed numbers with different denominators.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const pool = difficulty === 2 ? [2, 3, 4] : [3, 4, 5, 6, 8];
      const d1 = rng.pick(pool);
      const d2 = rng.pick(pool.filter((d) => d !== d1));
      const n1 = rng.int(1, d1 - 1);
      const n2 = rng.int(1, d2 - 1);
      const w1 = rng.int(1, difficulty === 4 ? 9 : 5);
      const w2 = rng.int(1, difficulty === 4 ? 9 : 5);

      const a = fromMixed(w1, n1, d1);
      const b = fromMixed(w2, n2, d2);
      const answer = add(a, b);

      const denominatorsAdded = fromMixed(w1 + w2, n1 + n2, d1 + d2);
      const partsMultiplied = fromMixed(w1 + w2, n1 * n2, d1 * d2);
      const traps = [fractionTrap(denominatorsAdded, 'add-denominators')];
      if (toText(partsMultiplied) !== toText(denominatorsAdded)) {
        traps.push(fractionTrap(partsMultiplied, 'cross-multiply-to-add'));
      }

      return fractionExercise({
        prompt: 'Add, writing your answer as a mixed number.',
        latex: `${toLatex(a, { mixed: true })} + ${toLatex(b, { mixed: true })}`,
        value: answer,
        summary: `Over a common denominator the fraction parts are ${n1 * (d2 / gcd(d1, d2))}/${(d1 / gcd(d1, d2)) * d2} and ${n2 * (d1 / gcd(d1, d2))}/${(d1 / gcd(d1, d2)) * d2}, and the whole answer is ${mixedText(answer)}.`,
        hint: 'Give the two fraction parts the same denominator first. Then add wholes to wholes and parts to parts.',
        steps: [
          { label: 'Add the whole numbers.', latex: `${w1} + ${w2} = ${w1 + w2}` },
          { label: 'Give the fraction parts a common denominator.', latex: `\\frac{${n1}}{${d1}} + \\frac{${n2}}{${d2}}` },
          { label: 'Add them.', latex: toLatex(add(fraction(n1, d1), fraction(n2, d2))) },
          { label: 'Put the parts together, trading a whole if the fraction part is one or more.', latex: toLatex(answer, { mixed: true }) },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'subtract-mixed-borrow',
    conceptId: 'add-mixed-numbers',
    description: 'Subtract mixed numbers where a whole has to be borrowed.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const d = rng.pick(difficulty === 3 ? [4, 5, 6] : [5, 6, 8, 10, 12]);
      const n1 = rng.int(1, d - 2);
      const n2 = rng.int(n1 + 1, d - 1);
      const w2 = rng.int(1, 4);
      const w1 = rng.int(w2 + 1, w2 + 5);

      const a = fromMixed(w1, n1, d);
      const b = fromMixed(w2, n2, d);
      const answer = sub(a, b);

      return fractionExercise({
        prompt: 'Subtract, writing your answer as a mixed number.',
        latex: `${toLatex(a, { mixed: true })} - ${toLatex(b, { mixed: true })}`,
        value: answer,
        summary: `There are not enough ${d}ths to take ${n2} from ${n1}, so one whole is traded for ${d} of them: ${w1} ${n1}/${d} is ${w1 - 1} ${n1 + d}/${d}.`,
        hint: `You cannot take ${n2}/${d} from ${n1}/${d}. Borrow a whole from the ${w1} and turn it into ${d}ths.`,
        steps: [
          { label: 'The fraction being taken away is the larger one.', latex: `\\frac{${n2}}{${d}} > \\frac{${n1}}{${d}}` },
          { label: 'Borrow one whole and write it as pieces.', latex: `${w1}\\tfrac{${n1}}{${d}} = ${w1 - 1}\\tfrac{${n1 + d}}{${d}}` },
          { label: 'Now subtract wholes and parts.', latex: `${w1 - 1 - w2}\\tfrac{${n1 + d - n2}}{${d}}` },
          { label: 'Simplify if you can.', latex: toLatex(answer, { mixed: true }) },
        ],
        // Taking the smaller fraction from the larger to avoid the borrow.
        traps: [fractionTrap(fromMixed(w1 - w2, n2 - n1, d), 'mixed-subtraction-not-borrowed')],
      });
    },
  }),

  template({
    id: 'add-mixed-regroup-form',
    conceptId: 'add-mixed-numbers',
    description: 'Choose the correctly regrouped mixed number.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.pick(difficulty === 1 ? [3, 5] : [5, 7, 9]);
      // Both parts in lowest terms, and their sum past a whole, so a trade is
      // genuinely needed and no option is given away by how it is written.
      const [n1, n2] = rng.pick(overflowingPairs(d));
      const w1 = rng.int(1, 6);
      const w2 = rng.int(1, 6);
      const answer = add(fromMixed(w1, n1, d), fromMixed(w2, n2, d));

      const { choices, correctId } = options(
        [
          { label: mixedText(answer), correct: true },
          { label: `${w1 + w2} ${n1 + n2}/${d}`, misconceptionId: 'mixed-regroup-missed' },
          { label: `${w1 + w2} ${n1 + n2}/${2 * d}`, misconceptionId: 'add-denominators' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which is ${w1} ${n1}/${d} + ${w2} ${n2}/${d} written properly as a mixed number?`,
        choices,
        correctId,
        summary: `The fraction parts add to ${n1 + n2}/${d}, which is more than one whole, so a whole is traded across to give ${mixedText(answer)}.`,
        hint: 'A mixed number is not finished while its fraction part is a whole one or more.',
        steps: [
          { label: 'Add the wholes.', latex: `${w1} + ${w2} = ${w1 + w2}` },
          { label: 'Add the parts.', latex: `\\frac{${n1}}{${d}} + \\frac{${n2}}{${d}} = \\frac{${n1 + n2}}{${d}}` },
          { label: 'That is more than one whole, so trade.', latex: `\\frac{${n1 + n2}}{${d}} = 1\\tfrac{${n1 + n2 - d}}{${d}}` },
          { label: 'Finish.', latex: toLatex(answer, { mixed: true }) },
        ],
      });
    },
  }),

  /* ---------------------------------------------- multiplication as scaling */
  template({
    id: 'scaling-below-one',
    conceptId: 'fraction-scaling',
    description: 'Compare a product with its factor, without multiplying.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const whole = rng.int(6, difficulty === 1 ? 30 : 90);
      const d = rng.pick([3, 4, 5, 6, 8, 10, 12]);
      const n = rng.int(1, d - 1);
      const { choices, correctId } = options(
        [
          { label: `less than ${whole}`, correct: true },
          { label: `greater than ${whole}`, misconceptionId: 'multiplication-always-grows' },
          { label: `equal to ${whole}`, misconceptionId: 'whole-number-fraction-confused' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Without multiplying, decide how the product compares with ${whole}.`,
        latex: `${whole} \\times \\frac{${n}}{${d}}`,
        choices,
        correctId,
        summary: `${n}/${d} is less than 1, so taking that much of ${whole} leaves less than ${whole}.`,
        hint: 'Compare the multiplier with 1. Is it a stretch or a squash?',
        steps: [
          { label: 'Compare the numerator with the denominator.', latex: `${n} < ${d}` },
          { label: 'So the multiplier is below 1.', latex: `\\frac{${n}}{${d}} < 1` },
          { label: 'Multiplying by less than 1 makes a number smaller.', latex: `${whole} \\times \\frac{${n}}{${d}} < ${whole}` },
        ],
      });
    },
  }),

  template({
    id: 'scaling-by-one-in-disguise',
    conceptId: 'fraction-scaling',
    description: 'Multiply by a fraction whose numerator and denominator match.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const whole = rng.int(4, difficulty === 1 ? 40 : 90);
      const d = rng.int(2, 12);
      return numericExercise({
        prompt: 'Work out the product.',
        latex: `${whole} \\times \\frac{${d}}{${d}}`,
        value: whole,
        summary: `${d}/${d} is one whole, so multiplying by it changes nothing: the answer is ${whole}.`,
        hint: 'What is a fraction worth when its numerator and denominator are the same?',
        steps: [
          { label: 'Read the multiplier.', latex: `\\frac{${d}}{${d}} = 1` },
          { label: 'Multiplying by 1 leaves a number alone.', latex: `${whole} \\times 1 = ${whole}` },
        ],
        traps: [trap(whole * d, 'multiplication-always-grows')],
      });
    },
  }),

  template({
    id: 'scaling-choose-multiplier',
    conceptId: 'fraction-scaling',
    description: 'Choose the multiplier that makes a number smaller or larger.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const whole = rng.int(8, difficulty === 2 ? 40 : 96);
      const d = rng.pick([3, 4, 5, 6, 8]);
      const small = rng.int(1, d - 1);
      const large = rng.int(d + 1, 2 * d);
      const wantSmaller = rng.bool();

      const { choices, correctId } = options(
        [
          { label: `${small}/${d}`, correct: wantSmaller, ...(wantSmaller ? {} : { misconceptionId: 'multiplication-always-grows' }) },
          { label: `${large}/${d}`, correct: !wantSmaller, ...(wantSmaller ? { misconceptionId: 'multiplication-always-grows' } : {}) },
          { label: `${d}/${d}`, misconceptionId: 'whole-number-fraction-confused' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which multiplier makes ${whole} ${wantSmaller ? 'smaller' : 'larger'}?`,
        choices,
        correctId,
        summary: wantSmaller
          ? `Only a multiplier below 1 shrinks a number, and ${small}/${d} is the one with a numerator smaller than its denominator.`
          : `Only a multiplier above 1 grows a number, and ${large}/${d} is the one with a numerator larger than its denominator.`,
        hint: 'Compare each numerator with its denominator.',
        steps: [
          { label: 'A multiplier below 1 shrinks.', latex: `\\frac{${small}}{${d}} < 1` },
          { label: 'A multiplier of exactly 1 changes nothing.', latex: `\\frac{${d}}{${d}} = 1` },
          { label: 'A multiplier above 1 grows.', latex: `\\frac{${large}}{${d}} > 1` },
        ],
      });
    },
  }),

  /* ---------------------------------------- dividing with unit fractions */
  template({
    id: 'whole-by-unit-fraction',
    conceptId: 'divide-unit-fractions',
    description: 'Divide a whole number by a unit fraction.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.int(2, difficulty === 1 ? 5 : 10);
      const multiple = rng.int(2, difficulty === 1 ? 6 : 12);
      const whole = d * multiple;
      const name = rng.pick(NAMES);
      const answer = whole * d;
      const traps = [trap(multiple, 'division-always-shrinks')];
      if (d !== multiple && d !== answer) traps.push(trap(d, 'intermediate-answer-given'));
      return numericExercise({
        prompt: `${name} cuts a ${whole} metre rope into pieces 1/${d} of a metre long. How many pieces are there?`,
        latex: `${whole} \\div \\frac{1}{${d}}`,
        value: answer,
        summary: `Each metre holds ${d} pieces, and there are ${whole} metres, so there are ${answer} pieces.`,
        hint: `How many pieces of 1/${d} of a metre fit into one metre? Now into ${whole} metres.`,
        steps: [
          { label: 'Ask how many fit into one whole.', latex: `1 \\div \\frac{1}{${d}} = ${d}` },
          { label: `There are ${whole} wholes.`, latex: `${whole} \\times ${d} = ${answer}` },
          { label: 'The answer is larger than the number you started with, because the pieces are small.', latex: `${answer} > ${whole}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'unit-fraction-by-whole',
    conceptId: 'divide-unit-fractions',
    description: 'Divide a unit fraction by a whole number.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.int(2, difficulty === 2 ? 6 : 10);
      const parts = rng.int(2, difficulty === 2 ? 5 : 9);
      const answer = fraction(1, d * parts);
      return fractionExercise({
        prompt: `1/${d} of a litre of paint is shared equally between ${parts} pots. How much paint is in each pot?`,
        latex: `\\frac{1}{${d}} \\div ${parts}`,
        value: answer,
        summary: `Cutting each ${d}th into ${parts} equal pieces makes ${d * parts}ths, so each pot holds 1/${d * parts} of a litre.`,
        hint: `Cut the ${d}th into ${parts} equal pieces. How many pieces of that size make one whole litre?`,
        steps: [
          { label: 'Cut the piece into smaller pieces.', latex: `\\frac{1}{${d}} \\div ${parts}` },
          { label: `Each ${d}th becomes ${parts} pieces, so a whole becomes ${d * parts}.`, latex: `${d} \\times ${parts} = ${d * parts}` },
          { label: 'Each pot holds one of them.', latex: toLatex(answer) },
          { label: 'Check by multiplying back.', latex: `${toLatex(answer)} \\times ${parts} = \\frac{1}{${d}}` },
        ],
        // Answering the other division: how many pots-worth fit into the paint.
        traps: [fractionTrap(fraction(d * parts, 1), 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'unit-fraction-division-choose',
    conceptId: 'divide-unit-fractions',
    description: 'Choose the calculation that answers a fraction-division problem.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.int(2, difficulty === 2 ? 5 : 9);
      const whole = rng.int(2, difficulty === 2 ? 6 : 12);
      const name = rng.pick(NAMES);

      const { choices, correctId } = options(
        [
          { label: `${whole} ÷ 1/${d}`, correct: true },
          { label: `1/${d} ÷ ${whole}`, misconceptionId: 'divide-wrong-direction' },
          { label: `${whole} × 1/${d}`, misconceptionId: 'division-always-shrinks' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${name} has ${whole} litres of soup and pours it into bowls holding 1/${d} of a litre. Which calculation gives the number of bowls?`,
        choices,
        correctId,
        summary: `The question counts how many 1/${d} litre bowls fit inside ${whole} litres, which is the division ${whole} ÷ 1/${d}.`,
        hint: 'Say the question in words: how many of the small amount fit inside the large one?',
        steps: [
          { label: 'The soup is being measured out in bowlfuls.', latex: `${whole} \\div \\frac{1}{${d}}` },
          { label: 'Each litre fills that many bowls.', latex: `${d} \\text{ bowls per litre}` },
          { label: 'So the answer is bigger than the number of litres.', latex: `${whole} \\times ${d} = ${whole * d}` },
        ],
      });
    },
  }),

  /* ------------------------------------------------ numerical expressions */
  template({
    id: 'evaluate-with-brackets',
    conceptId: 'numerical-expressions',
    description: 'Evaluate an expression containing brackets.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 9 : difficulty === 2 ? 15 : 25;
      const a = rng.int(2, difficulty === 1 ? 6 : 9);
      const b = rng.int(2, ceiling);
      const c = rng.int(2, ceiling);
      const answer = a * (b + c);
      return numericExercise({
        prompt: 'Work out the value of this expression.',
        latex: `${a} \\times (${b} + ${c})`,
        value: answer,
        summary: `The bracket is worth ${b + c}, and ${a} × ${b + c} = ${answer}.`,
        hint: 'The bracket is one number as far as the rest of the expression is concerned. Work it out first.',
        steps: [
          { label: 'Brackets first.', latex: `${b} + ${c} = ${b + c}` },
          { label: 'Now the multiplication.', latex: `${a} \\times ${b + c} = ${answer}` },
        ],
        traps: [trap(a * b + c, 'parentheses-ignored')],
      });
    },
  }),

  template({
    id: 'evaluate-mixed-precedence',
    conceptId: 'numerical-expressions',
    description: 'Evaluate an expression where the brackets change which operation comes first.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 12 : 20;
      const a = rng.int(2, ceiling);
      const b = rng.int(2, ceiling);
      const c = rng.int(4, 12);
      // The second bracket is worth at least 2, so the multiplication does
      // something. Multiplying by 1 makes the whole expression a decoration.
      const d = rng.int(1, c - 2);
      const answer = (a + b) * (c - d);
      const traps = [];
      const ignored = a + b * c - d;
      if (ignored !== answer) traps.push(trap(ignored, 'parentheses-ignored'));
      return numericExercise({
        prompt: 'Work out the value of this expression.',
        latex: `(${a} + ${b}) \\times (${c} - ${d})`,
        value: answer,
        summary: `The two brackets are worth ${a + b} and ${c - d}, and ${a + b} × ${c - d} = ${answer}.`,
        hint: 'Two brackets, so two small calculations. Do both before you multiply.',
        steps: [
          { label: 'First bracket.', latex: `${a} + ${b} = ${a + b}` },
          { label: 'Second bracket.', latex: `${c} - ${d} = ${c - d}` },
          { label: 'Multiply the two results.', latex: `${a + b} \\times ${c - d} = ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'expression-from-words',
    conceptId: 'numerical-expressions',
    description: 'Choose the expression that matches a sentence.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 1 ? 6 : 9);
      const b = rng.int(2, difficulty === 1 ? 12 : 30);
      const c = rng.int(b + 1, difficulty === 1 ? 20 : 40);

      const { choices, correctId } = options(
        [
          { label: `${a} × (${b} + ${c})`, correct: true },
          { label: `${a} × ${b} + ${c}`, misconceptionId: 'parentheses-ignored' },
          { label: `${a} + ${b} × ${c}`, misconceptionId: 'multiply-then-add-wrong-order' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which expression means "${a} times the sum of ${b} and ${c}"?`,
        choices,
        correctId,
        summary: `"The sum of ${b} and ${c}" is one quantity, so it goes in brackets and the whole of it is multiplied by ${a}.`,
        hint: 'What is being multiplied by the first number — one of the numbers, or both of them together?',
        steps: [
          { label: 'Find the part the words bundle together.', latex: `${b} + ${c}` },
          { label: 'Put brackets round it.', latex: `(${b} + ${c})` },
          { label: 'Multiply the whole bracket.', latex: `${a} \\times (${b} + ${c}) = ${a * (b + c)}` },
        ],
      });
    },
  }),

  /* ------------------------------------------------------ pattern pairs */
  template({
    id: 'pattern-pairs-term',
    conceptId: 'number-pattern-pairs',
    description: 'Find a term of the second of two related sequences.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const stepA = rng.int(2, difficulty === 1 ? 5 : 9);
      const factor = rng.int(2, difficulty === 3 ? 6 : 4);
      const stepB = stepA * factor;
      const term = rng.int(4, difficulty === 1 ? 7 : 12);
      const answer = stepB * term;

      const traps = [trap(stepB * (term - 1), 'pattern-rule-misread')];
      const additive = stepA * term + (stepB - stepA);
      if (additive !== answer && additive !== stepB * (term - 1)) {
        traps.push(trap(additive, 'comparison-read-as-additive'));
      }

      return numericExercise({
        prompt: `Sequence A starts at 0 and adds ${stepA} each time. Sequence B starts at 0 and adds ${stepB} each time. What is the ${term}th term of B?`,
        value: answer,
        summary: `B gains ${stepB} at every step, so its ${term}th term is ${term} × ${stepB} = ${answer}.`,
        hint: 'Count the steps taken from 0, then multiply by the size of one step.',
        steps: [
          { label: 'B has taken this many steps from 0.', latex: String(term) },
          { label: 'Each step is worth this much.', latex: String(stepB) },
          { label: 'Multiply.', latex: `${term} \\times ${stepB} = ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'pattern-pairs-multiplier',
    conceptId: 'number-pattern-pairs',
    description: 'Find how many times one sequence is the other.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const stepA = rng.int(2, difficulty === 2 ? 6 : 12);
      const factor = rng.int(2, difficulty === 4 ? 8 : 5);
      const stepB = stepA * factor;
      const traps = stepB - stepA === factor ? [] : [trap(stepB - stepA, 'comparison-read-as-additive')];
      return numericExercise({
        prompt: `Sequence A starts at 0 and adds ${stepA}. Sequence B starts at 0 and adds ${stepB}. Each term of B is how many times the matching term of A?`,
        value: factor,
        summary: `B gains ${stepB} for every ${stepA} that A gains, and ${stepB} ÷ ${stepA} = ${factor}, so B stays ${factor} times A.`,
        hint: 'Compare the two step sizes. How many of the smaller step make the larger one?',
        steps: [
          { label: 'Compare one step of each.', latex: `${stepB} \\div ${stepA}` },
          { label: 'Divide.', latex: String(factor) },
          { label: 'Both started at 0, so the ratio holds all the way along.', latex: `${stepB} = ${factor} \\times ${stepA}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'pattern-pairs-ordered-pair',
    conceptId: 'number-pattern-pairs',
    description: 'Write a pair of corresponding terms as an ordered pair.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const stepA = rng.int(2, difficulty === 2 ? 5 : 9);
      const factor = rng.int(2, 5);
      const stepB = stepA * factor;
      const term = rng.int(2, difficulty === 4 ? 9 : 5);
      const a = stepA * term;
      const b = stepB * term;

      const { choices, correctId } = options(
        [
          { label: `(${a}, ${b})`, correct: true },
          { label: `(${b}, ${a})`, misconceptionId: 'coordinates-reversed' },
          { label: `(${a}, ${a + (stepB - stepA)})`, misconceptionId: 'comparison-read-as-additive' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Sequence A adds ${stepA} from 0; sequence B adds ${stepB} from 0. Which ordered pair holds their ${term}th terms, with A's term first?`,
        choices,
        correctId,
        summary: `A's ${term}th term is ${a} and B's is ${b}, so the pair is (${a}, ${b}).`,
        hint: 'Work out both terms, then write A first because the question says so.',
        steps: [
          { label: "A's term.", latex: `${term} \\times ${stepA} = ${a}` },
          { label: "B's term.", latex: `${term} \\times ${stepB} = ${b}` },
          { label: 'Write the pair in the order asked for.', latex: `(${a},\\ ${b})` },
        ],
      });
    },
  }),

  /* ------------------------------------------------- the first quadrant */
  template({
    id: 'read-coordinate-value',
    conceptId: 'coordinate-first-quadrant',
    description: 'Read one coordinate of a plotted point.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const max = difficulty === 1 ? 6 : 10;
      const x = rng.int(1, max - 1);
      const y = rng.int(1, max - 1);
      if (x === y) return buildDistinctPoint(rng, max);
      const askX = rng.bool();
      return numericExercise({
        prompt: askX ? 'How far across is point P?' : 'How far up is point P?',
        figure: { kind: 'coordinate-grid', min: 0, max, points: [{ x, y, label: 'P' }] },
        value: askX ? x : y,
        summary: `P sits ${x} across and ${y} up, so its ${askX ? 'x' : 'y'}-coordinate is ${askX ? x : y}.`,
        hint: askX ? 'Drop straight down from P to the across axis.' : 'Go straight left from P to the up axis.',
        steps: [
          { label: 'Read across first.', latex: `x = ${x}` },
          { label: 'Then up.', latex: `y = ${y}` },
          { label: 'The question asked for one of them.', latex: askX ? `x = ${x}` : `y = ${y}` },
        ],
        traps: [trap(askX ? y : x, 'coordinates-reversed')],
      });
    },
  }),

  template({
    id: 'name-the-point',
    conceptId: 'coordinate-first-quadrant',
    description: 'Name a plotted point as an ordered pair.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const max = difficulty === 1 ? 6 : 10;
      const x = rng.int(2, max - 1);
      let y = rng.int(2, max - 1);
      if (y === x) y = y === max - 1 ? y - 1 : y + 1;

      const { choices, correctId } = options(
        [
          { label: `(${x}, ${y})`, correct: true },
          { label: `(${y}, ${x})`, misconceptionId: 'coordinates-reversed' },
          { label: `(${x - 1}, ${y - 1})`, misconceptionId: 'origin-counted-as-one' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which ordered pair names the marked point?',
        figure: { kind: 'coordinate-grid', min: 0, max, points: [{ x, y }] },
        choices,
        correctId,
        summary: `The point is ${x} along and ${y} up, and an ordered pair is written (across, up), so it is (${x}, ${y}).`,
        hint: 'Along the corridor, then up the stairs. Count the units on each axis, starting from 0.',
        steps: [
          { label: 'Count across from the origin.', latex: `x = ${x}` },
          { label: 'Count up.', latex: `y = ${y}` },
          { label: 'Write across first.', latex: `(${x},\\ ${y})` },
        ],
      });
    },
  }),

  template({
    id: 'rectangle-fourth-vertex',
    conceptId: 'coordinate-first-quadrant',
    description: 'Find a coordinate of the missing corner of a rectangle.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const max = difficulty === 3 ? 8 : 10;
      const x1 = rng.int(1, max - 4);
      const x2 = rng.int(x1 + 2, max - 1);
      const y1 = rng.int(1, max - 4);
      let y2 = rng.int(y1 + 2, max - 1);
      if (y2 === x2) y2 = y2 - 1 > y1 ? y2 - 1 : y2 + 1;
      const askX = rng.bool();

      return numericExercise({
        prompt: `Three corners of a rectangle are marked. What is the ${askX ? 'x' : 'y'}-coordinate of the fourth corner?`,
        figure: {
          kind: 'coordinate-grid',
          min: 0,
          max,
          points: [
            { x: x1, y: y1, label: 'A' },
            { x: x2, y: y1, label: 'B' },
            { x: x1, y: y2, label: 'C' },
          ],
        },
        value: askX ? x2 : y2,
        summary: `The fourth corner is directly above B and directly right of C, so it is (${x2}, ${y2}).`,
        hint: 'The missing corner lines up with one marked corner across, and with another one up.',
        steps: [
          { label: 'It shares its across position with B.', latex: `x = ${x2}` },
          { label: 'It shares its up position with C.', latex: `y = ${y2}` },
          { label: 'The corner is here.', latex: `(${x2},\\ ${y2})` },
        ],
        traps: [trap(askX ? y2 : x2, 'coordinates-reversed')],
      });
    },
  }),

  /* --------------------------------------------------------------- volume */
  template({
    id: 'count-unit-cubes',
    conceptId: 'volume-unit-cubes',
    description: 'Find a volume by counting the unit cubes that fill a box.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 5 : difficulty === 2 ? 9 : 14;
      const length = rng.int(2, ceiling);
      const width = rng.int(2, ceiling);
      const height = rng.int(2, ceiling);
      const layer = length * width;
      const answer = layer * height;
      const edgeSum = length + width + height;

      const traps = [trap(layer, 'volume-as-area')];
      if (edgeSum !== layer) traps.push(trap(edgeSum, 'volume-edges-added'));

      return numericExercise({
        prompt: `How many centimetre cubes are needed to fill this box completely?`,
        figure: prismFigure(length, width, height),
        unit: 'cm³',
        value: answer,
        summary: `The bottom layer holds ${length} × ${width} = ${layer} cubes, and there are ${height} layers, so ${answer} cubes fill the box.`,
        hint: 'How many cubes cover the floor of the box? Now how many of those layers are stacked up?',
        steps: [
          { label: 'One layer covers the base.', latex: `${length} \\times ${width} = ${layer}` },
          { label: 'Count the layers.', latex: String(height) },
          { label: 'Multiply.', latex: `${layer} \\times ${height} = ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'volume-from-layers',
    conceptId: 'volume-unit-cubes',
    description: 'Find a volume from the size of one layer.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const layer = rng.int(6, difficulty === 1 ? 20 : 60);
      const height = rng.int(2, difficulty === 3 ? 12 : 7);
      const answer = layer * height;
      const traps = [trap(layer, 'volume-as-area')];
      if (layer + height !== answer && layer + height !== layer) {
        traps.push(trap(layer + height, 'volume-edges-added'));
      }
      return numericExercise({
        prompt: `The bottom layer of a box holds ${layer} centimetre cubes, and the box is ${height} layers tall. What is its volume?`,
        unit: 'cm³',
        value: answer,
        summary: `Every layer holds ${layer} cubes and there are ${height} of them, so the volume is ${answer} cm³.`,
        hint: 'Volume is the area of the base multiplied by how many layers stand on it.',
        steps: [
          { label: 'One layer.', latex: String(layer) },
          { label: 'Multiply by the number of layers.', latex: `${layer} \\times ${height} = ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'volume-cubes-missing-edge',
    conceptId: 'volume-unit-cubes',
    description: 'Find how many layers tall a box is, from its volume.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const length = rng.int(2, difficulty === 4 ? 8 : 12);
      const width = rng.int(2, difficulty === 4 ? 8 : 10);
      const height = rng.int(2, 12);
      const layer = length * width;
      const volume = layer * height;
      const traps = layer === height ? [] : [trap(layer, 'volume-as-area')];
      return numericExercise({
        prompt: `A box holds ${volume} centimetre cubes. Its base is ${length} cm by ${width} cm. How many layers of cubes tall is it?`,
        unit: 'cm',
        value: height,
        summary: `Each layer holds ${length} × ${width} = ${layer} cubes, and ${volume} ÷ ${layer} = ${height} layers.`,
        hint: 'Work out how many cubes are in one layer, then see how many of those layers make the total.',
        steps: [
          { label: 'Cubes in one layer.', latex: `${length} \\times ${width} = ${layer}` },
          { label: 'Divide the total by that.', latex: `${volume} \\div ${layer} = ${height}` },
          { label: 'A height is a length, so it is not cubed.', latex: `${height} \\text{ cm}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'composite-two-prisms',
    conceptId: 'volume-composite',
    description: 'Find the volume of a solid made from two stacked boxes.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 5 : difficulty === 2 ? 8 : 12;
      const width = rng.int(2, ceiling);
      const lowerLength = rng.int(4, ceiling + 4);
      const lowerHeight = rng.int(2, ceiling);
      const upperLength = rng.int(2, lowerLength - 1);
      const upperHeight = rng.int(2, ceiling);

      const lower = lowerLength * width * lowerHeight;
      const upper = upperLength * width * upperHeight;
      const answer = lower + upper;
      const wholeBox = lowerLength * width * (lowerHeight + upperHeight);

      const traps = [trap(wholeBox, 'composite-solid-as-one-box')];
      if (lower !== wholeBox && lower !== answer) traps.push(trap(lower, 'intermediate-answer-given'));

      return numericExercise({
        prompt: `A step is made from two boxes. The lower box is ${lowerLength} cm by ${width} cm by ${lowerHeight} cm tall. A second box, ${upperLength} cm by ${width} cm by ${upperHeight} cm tall, stands on one end of it. What is the volume of the whole step?`,
        unit: 'cm³',
        value: answer,
        summary: `The two boxes hold ${lower} cm³ and ${upper} cm³, and volume is additive, so the step holds ${answer} cm³.`,
        hint: 'Find each box on its own, then add. No cube belongs to both.',
        steps: [
          { label: 'The lower box.', latex: `${lowerLength} \\times ${width} \\times ${lowerHeight} = ${lower}` },
          { label: 'The upper box.', latex: `${upperLength} \\times ${width} \\times ${upperHeight} = ${upper}` },
          { label: 'Add them.', latex: `${lower} + ${upper} = ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'composite-subtract-piece',
    conceptId: 'volume-composite',
    description: 'Find the volume of a solid with a corner removed.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 6 : 12;
      const length = rng.int(5, ceiling + 4);
      const width = rng.int(2, ceiling);
      const height = rng.int(2, ceiling);
      // Never exactly half: the piece removed would then equal the piece left,
      // and the distractor that catches "answered about the wrong piece" is
      // silently dropped for coinciding with the correct answer.
      const halved = length % 2 === 0 ? length / 2 : -1;
      const cut = rng.pick([...Array(length - 3).keys()].map((i) => i + 2).filter((value) => value !== halved));

      const whole = length * width * height;
      const removed = cut * width * height;
      const answer = whole - removed;

      const traps = [trap(whole, 'composite-solid-as-one-box')];
      if (removed !== answer && removed !== whole) traps.push(trap(removed, 'intermediate-answer-given'));

      return numericExercise({
        prompt: `A block ${length} cm by ${width} cm by ${height} cm has a piece ${cut} cm long cut off the end, right through the block. What is the volume of the piece that is left?`,
        unit: 'cm³',
        value: answer,
        summary: `The whole block was ${whole} cm³ and the piece removed was ${removed} cm³, so ${whole} − ${removed} = ${answer} cm³ remain.`,
        hint: 'Find the volume of the whole block, then take away the part that was cut off.',
        steps: [
          { label: 'The whole block.', latex: `${length} \\times ${width} \\times ${height} = ${whole}` },
          { label: 'The piece cut off.', latex: `${cut} \\times ${width} \\times ${height} = ${removed}` },
          { label: 'Subtract.', latex: `${whole} - ${removed} = ${answer}` },
          { label: 'Or measure what is left directly.', latex: `${length - cut} \\times ${width} \\times ${height} = ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'composite-find-removed',
    conceptId: 'volume-composite',
    description: 'Find the volume of the piece missing from a solid.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 4 ? 8 : 12;
      const length = rng.int(5, ceiling + 4);
      const width = rng.int(2, ceiling);
      const height = rng.int(2, ceiling);
      const cut = rng.int(2, length - 2);

      const whole = length * width * height;
      const remaining = (length - cut) * width * height;
      const answer = whole - remaining;

      const traps = [trap(whole, 'composite-solid-as-one-box')];
      if (remaining !== answer && remaining !== whole) traps.push(trap(remaining, 'intermediate-answer-given'));

      return numericExercise({
        prompt: `A block was ${length} cm by ${width} cm by ${height} cm before a piece was cut off the end, right through the block. What is left has a volume of ${remaining} cm³. What was the volume of the piece cut off?`,
        unit: 'cm³',
        value: answer,
        summary: `The whole block held ${whole} cm³, and ${whole} − ${remaining} = ${answer} cm³ went with the piece that was cut off.`,
        hint: 'The two pieces together are the block you started with.',
        steps: [
          { label: 'The block before the cut.', latex: `${length} \\times ${width} \\times ${height} = ${whole}` },
          { label: 'Take away what is left.', latex: `${whole} - ${remaining} = ${answer}` },
          { label: 'Check: the cut was this long.', latex: `${answer} \\div (${width} \\times ${height}) = ${cut}` },
        ],
        traps,
      });
    },
  }),

  /* --------------------------------------------------- classifying shapes */
  template({
    id: 'shape-hierarchy-statement',
    conceptId: 'classify-quadrilaterals',
    description: 'Decide whether one shape family always belongs to another.',
    difficulties: [1, 2, 3, 4],
    build: ({ rng }) => {
      const first = rng.pick(FAMILIES);
      const second = rng.pick(FAMILIES.filter((family) => family.id !== first.id));
      const alwaysTrue = first.contains.includes(second.id);
      const reversed = second.contains.includes(first.id);

      const wrongId = alwaysTrue ? 'square-not-a-rectangle' : reversed ? 'hierarchy-inverted' : 'shape-named-by-look';

      const { choices, correctId } = options(
        [
          { label: 'Always true', correct: alwaysTrue, ...(alwaysTrue ? {} : { misconceptionId: wrongId }) },
          { label: 'Not always true', correct: !alwaysTrue, ...(alwaysTrue ? { misconceptionId: wrongId } : {}) },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Is this always true? Every ${first.name} is a ${second.name}.`,
        choices,
        correctId,
        summary: alwaysTrue
          ? `A ${first.name} meets every requirement for a ${second.name}, so it is one — a particular one.`
          : reversed
            ? `It is the other way round: every ${second.name} is a ${first.name}, but a ${first.name} need not be a ${second.name}.`
            : `A ${first.name} does not have to meet the requirements for a ${second.name}, so the statement fails for some ${first.plural}.`,
        hint: `List what a ${second.name} must have. Does every single ${first.name} have all of it?`,
        steps: [
          { label: `Write down what a ${second.name} requires.`, latex: '\\text{requirements}' },
          { label: `Check them against every ${first.name}, not a picture of one.`, latex: '\\text{check}' },
          { label: 'Adding a requirement makes a family smaller, so containment runs one way.', latex: alwaysTrue ? '\\text{true}' : '\\text{false}' },
        ],
      });
    },
  }),

  template({
    id: 'shape-most-precise-name',
    conceptId: 'classify-quadrilaterals',
    description: 'Name a quadrilateral from its properties.',
    difficulties: [1, 2, 3],
    build: ({ rng }) => {
      const item = rng.pick(DESCRIPTIONS);
      const { choices, correctId } = options(
        [
          { label: item.answer, correct: true },
          ...item.wrong.map((entry) => ({ label: entry.name, misconceptionId: entry.misconceptionId })),
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `A quadrilateral has ${item.properties}. What is it?`,
        choices,
        correctId,
        summary: `Those properties are exactly the requirements for a ${item.answer}, and they rule the other names out.`,
        hint: 'Take each name in turn and check it against every property given.',
        steps: [
          { label: 'Read the properties as a list of requirements.', latex: '\\text{requirements}' },
          { label: 'Test each candidate name against all of them.', latex: '\\text{test}' },
          { label: 'One name survives.', latex: `\\text{${item.answer}}` },
        ],
      });
    },
  }),

  template({
    id: 'shape-family-property',
    conceptId: 'classify-quadrilaterals',
    description: 'Decide whether every member of a family has a given property.',
    difficulties: [2, 3, 4],
    build: ({ rng }) => {
      const property = rng.pick(PROPERTIES);
      const family = rng.pick(FAMILIES.filter((entry) => entry.id !== 'quadrilateral'));
      const holds = property.holds.includes(family.id);

      const { choices, correctId } = options(
        [
          { label: 'Yes, always', correct: holds, ...(holds ? {} : { misconceptionId: 'shape-named-by-look' }) },
          { label: 'No, not always', correct: !holds, ...(holds ? { misconceptionId: 'square-not-a-rectangle' } : {}) },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Does every ${family.name} have ${property.text}?`,
        choices,
        correctId,
        summary: holds
          ? `Yes — ${property.text} is part of what makes a shape a ${family.name}.`
          : `No — some ${family.plural} have ${property.text} and others do not, so it is not a requirement.`,
        hint: `Try to picture a ${family.name} without ${property.text}. If you can draw one, the answer is no.`,
        steps: [
          { label: `List what a ${family.name} must have.`, latex: '\\text{requirements}' },
          { label: 'Ask whether the property is on that list.', latex: holds ? '\\text{yes}' : '\\text{no}' },
        ],
      });
    },
  }),
];

/**
 * A fallback point for `read-coordinate-value` when x and y come out equal.
 *
 * Equal coordinates make the reversed-coordinates trap identical to the correct
 * answer, so the one distractor worth having would be silently dropped and the
 * question would test nothing.
 */
function buildDistinctPoint(rng: { int(min: number, max: number): number; bool(): boolean }, max: number) {
  const x = rng.int(1, max - 2);
  const y = x + 1;
  const askX = rng.bool();
  return numericExercise({
    prompt: askX ? 'How far across is point P?' : 'How far up is point P?',
    figure: { kind: 'coordinate-grid', min: 0, max, points: [{ x, y, label: 'P' }] },
    value: askX ? x : y,
    summary: `P sits ${x} across and ${y} up, so its ${askX ? 'x' : 'y'}-coordinate is ${askX ? x : y}.`,
    hint: askX ? 'Drop straight down from P to the across axis.' : 'Go straight left from P to the up axis.',
    steps: [
      { label: 'Read across first.', latex: `x = ${x}` },
      { label: 'Then up.', latex: `y = ${y}` },
      { label: 'The question asked for one of them.', latex: askX ? `x = ${x}` : `y = ${y}` },
    ],
    traps: [trap(askX ? y : x, 'coordinates-reversed')],
  });
}
